/**
 * Conciliación: la tabla del mes, en una página entera.
 *
 * Ingresos y gastos se abre en los inmuebles —donde se sueltan los tickets, que es lo que más
 * se hace— y desde ahí «Ver conciliación» trae aquí. Con muchos movimientos la tabla necesita
 * la pantalla entera; compartirla con las tarjetas obligaba a bajar para cualquiera de las dos.
 *
 * LA TABLA, en el orden en que se cuadra a mano: nº, fecha, concepto, importe, saldo, estado
 * (Completo, Completo sin justificar, Pendiente), proveedor, inmueble, soporte documental y
 * observaciones. Se elige el mes y el año, se filtra por estado e inmueble, y pulsar una fila
 * abre lo que hay que hacer con ella. «Lo que falta» se abre en un panel lateral desde el pie.
 *
 * MAYO SIGUE AVISANDO DESDE JUNIO. Un mes conciliado al que le faltaba una factura no queda
 * «conciliado» sino «incompleto», y aparece arriba del todo con lo que faltó, mes tras mes,
 * hasta que alguien lo arregla.
 *
 * LOS INGRESOS SON LOS DEL BANCO. Lo cobrado sale del extracto, y la renta emitida queda como
 * referencia. Un mes sin extracto enseña cero cobrado, a propósito: «esperado» no es «cobrado».
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { BankMovement, LedgerEntry, LedgerGap, LedgerProperty } from '@naar/shared';
import { GASTOS_GENERALES_ID, GASTOS_GENERALES_NOMBRE, MONTH_NAMES_ES } from '@naar/shared';
import { ApiError, api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { ErrorState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { ConfirmDialog, Drawer } from '../components/overlay/Drawer';
import { toast } from '../components/feedback/Toaster';
import { useCan } from '../auth/AuthContext';
import { EntryEditDrawer } from './ledger/EntryEditDrawer';
import { MovementDrawer } from './ledger/MovementDrawer';
import { FilaDrawer } from './ledger/FilaDrawer';
import { AiReconciliationBanner } from './ledger/AiReconciliationBanner';
import { StatementUpload } from './ledger/StatementUpload';
import { MissingPanel } from './ledger/MissingPanel';
import { MovementTicketDrawer } from './ledger/MovementTicketDrawer';
import { OffBankPaymentDrawer } from './ledger/OffBankPaymentDrawer';
import { ReconciliationPanel } from './ledger/ReconciliationPanel';
import { filasDelMes, type FilaConciliacion } from './ledger/reconciliation';
import { MonthPicker } from './ledger/MonthPicker';
import { YearSummary } from './ledger/YearSummary';
import styles from './ledger/Ledger.module.css';

/**
 * El mes en curso según el calendario del usuario.
 *
 * Local y NO UTC: el 1 de junio a la 01:00 en Madrid son todavía las 23:00 del 31 de mayo en
 * UTC, y con UTC la aplicación abría mayo el día que el usuario ya está en junio. Y al abrirlo
 * creaba el cierre de mayo de paso. El mes es una fecha de calendario, no un instante.
 */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function ConciliationPage() {
  const queryClient = useQueryClient();
  const canManage = useCan('ledger:manage');
  /*
   * `?mes=2026-09` abre ese mes: es como llegan las tareas de Pendientes («Revisar la luz de
   * septiembre»). Sin él, el mes en curso.
   */
  const [searchParams] = useSearchParams();
  const mesPedido = searchParams.get('mes');
  const [month, setMonth] = useState(() =>
    mesPedido && /^\d{4}-(0[1-9]|1[0-2])$/.test(mesPedido) ? mesPedido : currentMonthKey(),
  );
  useEffect(() => {
    if (mesPedido && /^\d{4}-(0[1-9]|1[0-2])$/.test(mesPedido)) setMonth(mesPedido);
  }, [mesPedido]);
  const [editing, setEditing] = useState<{ entry: LedgerEntry; propertyName: string } | null>(null);
  /** Apuntar un gasto a mano: en un inmueble ya elegido, o eligiéndolo en el panel (`'elegir'`). */
  const [adding, setAdding] = useState<LedgerProperty | 'elegir' | null>(null);
  const [resolving, setResolving] = useState<BankMovement | null>(null);
  /** El ticket soltado en un cargo del banco, esperando a saber de qué inmueble es. */
  const [ticketDeCargo, setTicketDeCargo] = useState<{ movement: BankMovement; file: File } | null>(null);
  /** La renta sin cobrar que se está registrando como cobrada fuera del banco. */
  const [cobroFuera, setCobroFuera] = useState<{ gap: LedgerGap; propertyName: string; file: File | null } | null>(
    null,
  );
  const [confirmingClose, setConfirmingClose] = useState(false);
  /** «Todo»: la tabla enseña el año entero en vez de un mes. */
  const [todo, setTodo] = useState(false);
  /** Una fila pulsada en «Todo»: se salta a su mes y se abre en cuanto ese mes está cargado. */
  const [abrirTras, setAbrirTras] = useState<{ month: string; fila: FilaConciliacion } | null>(null);
  /**
   * La fila cuya ficha está abierta, por su clave. Se guarda la CLAVE y no la fila: tras guardar
   * se vuelve a pedir el mes y la ficha tiene que enseñar la fila nueva (con su documento nuevo),
   * no la copia de cuando se pulsó.
   */
  const [ficha, setFicha] = useState<string | null>(null);
  /** El panel lateral con la lista de lo que falta. */
  const [lista, setLista] = useState<'faltas' | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.ledgerMonth(month),
    queryFn: () => api.ledgerMonth(month),
  });

  /*
   * Cambiar de mes cierra lo que estuviera abierto.
   *
   * Los avisos de arriba llevan a otros meses de un clic, y un panel de edición sobrevivía al
   * salto: quedaba en pantalla un apunte de mayo, con el mes ya cambiado a junio, guardándose
   * contra el identificador de mayo. Mezclar dos meses en el mismo formulario es la clase de
   * error que solo se descubre cuadrando.
   */
  useEffect(() => {
    setLista(null);
    setEditing(null);
    setAdding(null);
    setResolving(null);
    setFicha(null);
    setTicketDeCargo(null);
    setCobroFuera(null);
    setConfirmingClose(false);
  }, [month]);

  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;

  /** Ir a un mes concreto: deja «Todo». */
  function irAMes(key: string) {
    setTodo(false);
    setMonth(key);
  }

  /*
   * «Todo»: los meses del año HASTA HOY (pedir un mes futuro abriría su cierre de paso). Son las
   * mismas consultas que el resumen del año, así que comparten caché y no se piden dos veces.
   */
  const hoy = currentMonthKey();
  const mesesDelAnio = todo
    ? Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`).filter((key) => key <= hoy)
    : [];
  const consultasAnio = useQueries({
    queries: mesesDelAnio.map((key) => ({
      queryKey: queryKeys.ledgerMonth(key),
      queryFn: () => api.ledgerMonth(key),
    })),
  });
  const anioCargando = consultasAnio.some((consulta) => consulta.isPending);
  const mesesCargados = consultasAnio.flatMap((consulta) => (consulta.data ? [consulta.data] : []));

  /*
   * La fila pulsada en «Todo», abierta ya en su mes. Va DESPUÉS del efecto que cierra los paneles
   * al cambiar de mes: los efectos corren en orden y, al revés, el panel se cerraría nada más abrirse.
   */
  useEffect(() => {
    if (!abrirTras || !data || data.month !== abrirTras.month) return;
    const { fila } = abrirTras;
    setAbrirTras(null);
    if (fila.prevista) {
      setLista('faltas');
      return;
    }
    // En «Todo» la clave lleva el mes delante («2026-10|e:…»): la del mes va sin él.
    const barra = fila.key.indexOf('|');
    setFicha(barra >= 0 ? fila.key.slice(barra + 1) : fila.key);
  }, [abrirTras, data]);

  const filaAbierta = useMemo(
    () => (data && ficha ? (filasDelMes(data).filas.find((fila) => fila.key === ficha) ?? null) : null),
    [data, ficha],
  );

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: ['ledger'] });
  }

  const reconcile = useMutation({
    mutationFn: () => api.reconcileMonth(month, {}),
    onSuccess: async (result) => {
      await invalidate();
      setConfirmingClose(false);
      if (result.status === 'conciliado') {
        toast.success('Mes conciliado y sin huecos.');
      } else {
        // No es un error: el mes se cierra igual. Pero se dice en cuántos sitios faltaba algo.
        toast.info(
          `Mes cerrado con ${result.gapCount} ${result.gapCount === 1 ? 'hueco' : 'huecos'}. Te lo recordaré.`,
        );
      }
    },
    onError: (fault: unknown) => {
      setConfirmingClose(false);
      toast.error(fault instanceof ApiError ? fault.message : 'No se ha podido conciliar el mes.');
    },
  });

  /**
   * Descargar los papeles del mes o del año en un ZIP por carpetas. Puede tardar unos segundos
   * con un año entero: el botón lo dice mientras tanto, para que nadie pulse dos veces.
   */
  const descargar = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => api.downloadLedgerExport(from, to),
    onError: (fault: unknown) =>
      toast.error(fault instanceof ApiError ? fault.message : 'No se ha podido preparar la descarga.'),
  });

  const reopen = useMutation({
    mutationFn: () => api.reopenMonth(month),
    onSuccess: async () => {
      await invalidate();
      toast.success('Mes reabierto.');
    },
    onError: () => toast.error('No se ha podido reabrir el mes.'),
  });

  const years = useMemo(() => data?.years ?? [year], [data, year]);

  const propiedades = data?.properties ?? [];
  const sinExtracto = Boolean(data && data.bank.statements.length === 0 && data.bank.movements.length === 0);
  const conciliado = data?.close.status === 'conciliado';
  /*
   * Mientras hay otro panel encima (corregir un apunte, resolver un movimiento…), el de la
   * lista se esconde y vuelve al cerrarlo: dos paneles apilados se pelean por el Escape.
   */
  const otroPanel = Boolean(editing || adding || resolving || ticketDeCargo || cobroFuera);

  /** Los papeles del mes o del año, en un ZIP con una carpeta por mes. */
  const descargas = canManage ? (
    <>
      {!todo && (
      <button
        type="button"
        className={styles.downloadButton}
        disabled={descargar.isPending}
        onClick={() => descargar.mutate({ from: month, to: month })}
      >
        <Icon name="download" size={14} strokeWidth={2} />
        {descargar.isPending ? 'Preparando…' : `Descargar ${MONTH_NAMES_ES[monthIndex]?.toLowerCase()}`}
      </button>
      )}
      <button
        type="button"
        className={styles.downloadButton}
        disabled={descargar.isPending}
        onClick={() => descargar.mutate({ from: `${year}-01`, to: `${year}-12` })}
      >
        {todo ? `Descargar ${year}` : `Todo ${year}`}
      </button>
    </>
  ) : undefined;

  /** Las acciones del mes, dentro del panel de conciliación: es ahí donde se decide cerrar. */
  const acciones =
    canManage && data && !todo ? (
      conciliado ? (
        <Button variant="secondary" size="sm" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
          Reabrir el mes
        </Button>
      ) : (
        <>
          <StatementUpload
            month={month}
            onImported={async () => {
              await invalidate();
              await queryClient.invalidateQueries({ queryKey: ['rent-charges'] });
            }}
            onGoToMonth={irAMes}
          />
          {data.close.status === 'incompleto' && (
            <Button variant="secondary" size="sm" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
              Reabrir
            </Button>
          )}
          <Button size="sm" onClick={() => setConfirmingClose(true)}>
            <Icon name="check" size={15} strokeWidth={2.4} />
            Conciliar {MONTH_NAMES_ES[monthIndex]?.toLowerCase()}
          </Button>
        </>
      )
    ) : undefined;

  return (
    <section>
      <PageHeader
        title="Conciliación"
        subtitle="Cada movimiento del mes, con su estado. Elige el mes, filtra por estado o inmueble y pulsa una fila para resolverla."
        backTo="/ingresos-y-gastos"
        backLabel="Ingresos y gastos"
      />

      {isPending && (
        <>
          <SkeletonCard rows={4} height={64} />
          <SkeletonCard rows={3} height={54} />
        </>
      )}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <>
          {/* --- Meses anteriores que siguen sin cuadrar -------------------- */}
          {data.pendingMonths.length > 0 && (
            <div className={styles.pending}>
              <Icon name="bell" size={16} />
              <div>
                <strong>Hay meses sin cuadrar.</strong>{' '}
                {data.pendingMonths.map((row, index) => (
                  <span key={row.month}>
                    {index > 0 && ', '}
                    <button type="button" className={styles.pendingLink} onClick={() => irAMes(row.month)}>
                      {MONTH_NAMES_ES[Number(row.month.slice(5, 7)) - 1]} de {row.month.slice(0, 4)}
                    </button>{' '}
                    ({row.gapCount})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/*
            Lo que faltaba el día que se concilió, congelado. «Incompleto» NO es cerrado: se puede
            seguir subiendo todo y volver a conciliar.
          */}
          {!todo && data.close.status === 'incompleto' && data.close.missingNotes && (
            <div className={styles.closedGaps}>
              <Icon name="info" size={16} />
              <div className={styles.closedBody}>
                <strong>Al conciliar faltaba:</strong>
                <ul className={styles.missingList}>
                  {/* Por índice: dos apuntes «Comunidad» sin factura del mismo inmueble dan
                      dos líneas idénticas, y con la línea como clave React ve duplicados. */}
                  {data.close.missingNotes.split('\n').map((line, index) => (
                    <li key={index}>{line}</li>
                  ))}
                </ul>
                <p className={styles.closedHint}>
                  El mes sigue abierto para completarlo: sube lo que falte y vuelve a conciliar.
                </p>
              </div>
            </div>
          )}

          {/* El resumen del año hasta la última fecha de la tabla, encima de ella. */}
          <YearSummary month={todo ? (`${year}-12` <= hoy ? `${year}-12` : hoy) : month} />

          <ReconciliationPanel
            month={month}
            data={data}
            canManage={canManage}
            actions={acciones}
            onOpenEntry={(entry, propertyName) => setEditing({ entry, propertyName })}
            onOpenMovement={setResolving}
            onOpenFila={(fila) => setFicha(fila.key)}
            onAddManual={() => setAdding('elegir')}
            year={todo ? { months: mesesCargados, loading: anioCargando } : undefined}
            onOpenInMonth={(key, fila) => {
              irAMes(key);
              setAbrirTras({ month: key, fila });
            }}
            period={
              <MonthPicker
                month={month}
                years={years}
                pendingMonths={data.pendingMonths}
                onChange={irAMes}
                // Pulsar un año enseña el año entero, como «Todo»: es lo que se busca al elegirlo.
                onYearChange={(key) => {
                  setMonth(key);
                  setTodo(true);
                }}
                all={todo}
                onAll={() => setTodo(true)}
              />
            }
            downloads={descargas}
            onShowMissing={() => setLista('faltas')}
            aiBanner={
              todo
                ? undefined
                : (verDudas) => (
                    <AiReconciliationBanner
                      month={month}
                      bank={data.bank}
                      canManage={canManage}
                      closed={conciliado}
                      onShowDoubts={verDudas}
                    />
                  )
            }
          />


        </>
      )}

      {/* --- Lo que falta y el extracto, en un panel lateral ----------------- */}
      {data && lista === 'faltas' && (
        <Drawer
          open={!otroPanel}
          wide
          title="Lo que falta"
          subtitle="Suelta cada ticket o justificante en su fila: así no hay que adivinar de qué es."
          onClose={() => setLista(null)}
        >
          <MissingPanel
            bare
            month={month}
            data={data}
            canManage={canManage}
            onReviewEntry={(entry, propertyName) => setEditing({ entry, propertyName })}
            onResolveMovement={setResolving}
            onMovementTicket={(movement, file) => setTicketDeCargo({ movement, file })}
            onOffBankPayment={(gap, propertyName, file) => setCobroFuera({ gap, propertyName, file })}
          />
        </Drawer>
      )}


      {ticketDeCargo && data && (
        <MovementTicketDrawer
          key={ticketDeCargo.movement.id}
          movement={ticketDeCargo.movement}
          file={ticketDeCargo.file}
          properties={data.properties}
          onClose={() => setTicketDeCargo(null)}
        />
      )}

      {cobroFuera && (
        <OffBankPaymentDrawer
          key={cobroFuera.gap.rentChargeId ?? cobroFuera.gap.concept}
          month={month}
          gap={cobroFuera.gap}
          propertyName={cobroFuera.propertyName}
          file={cobroFuera.file}
          onClose={() => setCobroFuera(null)}
        />
      )}

      {filaAbierta && data && (
        <FilaDrawer
          key={filaAbierta.key}
          fila={filaAbierta}
          month={month}
          properties={data.properties.map((property) => ({ id: property.id, name: property.name }))}
          tenants={data.bank.tenantOptions}
          onClose={() => setFicha(null)}
          onResolve={(movimiento) => {
            setFicha(null);
            setResolving(movimiento);
          }}
        />
      )}

      {resolving && data && (
        <MovementDrawer
          key={resolving.id}
          movement={resolving}
          bank={data.bank}
          properties={data.properties}
          onClose={() => setResolving(null)}
        />
      )}

      {editing && (
        <EntryEditDrawer
          open
          month={month}
          entry={editing.entry}
          propertyName={editing.propertyName}
          properties={[
            ...propiedades.map((row) => ({ id: row.id, name: row.name })),
            { id: GASTOS_GENERALES_ID, name: GASTOS_GENERALES_NOMBRE },
          ]}
          currentPropertyId={propiedades.find((row) => row.entries.some((e) => e.id === editing.entry.id))?.id}
          onClose={() => setEditing(null)}
        />
      )}

      {adding && (
        <EntryEditDrawer
          open
          month={month}
          {...(adding === 'elegir'
            ? {
                propertyName: 'Elige el inmueble',
                // Con «Gastos generales» al final: un apunte a mano puede no ser de ningún piso.
                properties: [
                  ...propiedades.map((row) => ({ id: row.id, name: row.name })),
                  { id: GASTOS_GENERALES_ID, name: GASTOS_GENERALES_NOMBRE },
                ],
              }
            : { propertyId: adding.id, propertyName: adding.name })}
          onClose={() => setAdding(null)}
        />
      )}

      {confirmingClose && data && (
        <ConfirmDialog
          title={`Conciliar ${MONTH_NAMES_ES[monthIndex]} de ${year}`}
          body={
            /*
             * Sin extracto no se puede decir que el mes cuadra: se puede cerrar, pero queda
             * incompleto, y el diálogo lo dice ANTES en vez de prometer «está todo».
             */
            sinExtracto && data.totals.gapCount === 0
              ? 'No falta ningún ticket, pero todavía no has subido el extracto del banco. Puedes cerrar igual: el mes quedará incompleto hasta que lo subas. Y los tickets que lleguen tarde se podrán seguir subiendo.'
              : data.totals.gapCount === 0
                ? 'Está todo: no falta ningún ticket ni queda nada sin revisar. Si después llega algún ticket, podrás subirlo igual y el mes volverá a pedirte que lo revises.'
                : `Faltan ${data.totals.gapCount + (sinExtracto ? 1 : 0)} cosas${sinExtracto ? ', entre ellas el extracto del banco' : ''}. Puedes cerrar igual — el mes quedará marcado como incompleto y te recordaré qué faltó cada vez que vuelvas.`
          }
          confirmLabel={data.totals.gapCount === 0 && !sinExtracto ? 'Conciliar' : 'Cerrar de todos modos'}
          onCancel={() => setConfirmingClose(false)}
          onConfirm={async () => {
            await reconcile.mutateAsync();
          }}
        />
      )}
    </section>
  );
}
